'use client';

import { useEffect, useRef, type RefObject } from 'react';

/**
 * ODAK SÖZLEŞMESİ — katman ve menü yüzeylerinin TEK kaynağı.
 *
 * SINIF (tech-lead ölçümü 30.08): `role="dialog"` taşıyan bir yüzey açıldığında
 * odak içeri TAŞINMIYOR, Tab panelin dışına kaçıyor ve kapanışta odak `body`ye
 * düşüyordu. Tarih seçicide ölçüldü: takvim açıldıktan sonra ızgaraya varmak
 * için formun geri kalanı boyunca 12 Tab gerekiyordu. Kusur tek bileşende
 * değildi — üç katman ve dört menü aynı sözleşmenin farklı yarımlarını
 * uyguluyordu (kimi tuzak yazmış geri dönüşü unutmuş, kimi tersi).
 *
 * Sözleşme burada TEK yerde yazılır; her yüzey aynı kancayı çağırır. Kapı:
 * `focus-surface.guard.spec.ts` — yeni bir katman/menü eklenip bu kancalara
 * bağlanmazsa test KIRILIR.
 *
 * İKİ AYAK (md. 1c): tuzak kullanıcıyı HAPSETMEZ. Panel Esc ile, seçimle ve
 * dışarı tıklamayla kapanmaya devam eder; kapandığında odak tetikleyiciye
 * döner (README 4.9).
 */

/**
 * Sekme sırasındaki öğeler. `[tabindex]` niteliği ELENMEZ, `tabIndex` DEĞERİ
 * okunur: gezinen (roving) desende pasif öğeler `-1` taşır ve sıraya girmez —
 * saat sütunundaki 96 seçenek tam olarak bu yüzden sıraya girmiyor.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
].join(', ');

/**
 * `tabIndex >= 0` OLMAK DURAK OLMAK DEĞİLDİR (QA, süzgeç çekmecesi turu).
 *
 * ÖLÇÜLMÜŞ KUSUR: çekmecede üç radyo grubu var; gerçek tarayıcıda Tab bir
 * radyo grubuna TEK KEZ uğrar (işaretli olana; hiçbiri işaretli değilse
 * ilkine) — bu tarayıcının kendi "roving tabindex" davranışıdır. Oysa
 * işaretsiz radyoların `tabIndex` DEĞERİ de 0'dır. Tarama 12 durak sayıyor,
 * tarayıcı 4'üne uğruyordu; `last` sandığımız öğeye Tab hiç gelmediği için
 * sarma kolu (`active === last → first.focus()`) TETİKLENMİYOR ve odak
 * perdenin altındaki "Süzgeç" düğmesine SIZIYORDU — üstelik orada Enter
 * çalışıyordu.
 *
 * Aynı körlüğün diğer örnekleri de burada kapanır: `[hidden]` ve `[inert]`
 * alt ağaçları odak ALMAZ ama içlerindeki düğmenin `tabIndex`i yine 0'dır.
 * Sınıf `focus-surface.guard.spec` içinde desen desen ENUMERE edilir.
 */
function radioGroupName(element: HTMLElement): string | null {
  if (!(element instanceof HTMLInputElement) || element.type !== 'radio') return null;
  /* Adsız radyo grup KURMAZ: tarayıcı her birine ayrı ayrı uğrar. */
  return element.name === '' ? null : element.name;
}

/** Grubun sekme durağı: işaretli olan, yoksa belge sırasındaki ilki. */
function radioStopOfGroup(group: readonly HTMLInputElement[]): HTMLInputElement | undefined {
  return group.find((radio) => radio.checked) ?? group[0];
}

export function focusableWithin(panel: HTMLElement): HTMLElement[] {
  const candidates = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.hasAttribute('disabled') &&
      element.getAttribute('aria-hidden') !== 'true' &&
      element.closest('[hidden], [inert]') === null,
  );

  const groups = new Map<string, HTMLInputElement[]>();
  for (const element of candidates) {
    const name = radioGroupName(element);
    if (name === null) continue;
    const group = groups.get(name) ?? [];
    group.push(element as HTMLInputElement);
    groups.set(name, group);
  }
  if (groups.size === 0) return candidates;

  const stops = new Set<HTMLElement>();
  for (const group of groups.values()) {
    const stop = radioStopOfGroup(group);
    if (stop) stops.add(stop);
  }
  return candidates.filter((element) => radioGroupName(element) === null || stops.has(element));
}

/**
 * GLOBAL DURUM YÜZEYİ — arka plan `inert` yapılırken ATLANIR.
 *
 * Çevrimdışı şeridi katmanın arkasında kalır ama bilgisi katmana AİT DEĞİLDİR;
 * `inert` konsaydı erişilebilirlik ağacından da düşer ve ekran okuyucu
 * bağlantının koptuğunu hiç duyurmazdı. Şerit bu niteliği taşır (`offline-bar`).
 */
export const GLOBAL_STATUS_ATTR = 'data-ib-global-status';

/** `inert`i BİZ koyduk mu — kalıcı `inert` taşıyan ağaçları sökmemek için. */
const INERT_MARK = 'data-ib-layer-inert';

/**
 * KAÇ KATMAN İŞARETLEDİ — İÇ İÇE KATMAN (tech-lead ölçümü 17.09).
 *
 * ÖLÇÜLMÜŞ KUSUR: işaret SAYILMIYOR, yalnız "zaten `inert` mi" diye
 * bakılıyordu. Katılımcı dizininde kenar katmanı (kişi paneli) ve içindeki
 * onay penceresi İÇ İÇEDİR; React efektleri ÇOCUKTAN EBEVEYNE koştuğu için
 * ikisi aynı çizimde açıldığında arka planı İÇTEKİ işaretliyor, dıştaki
 * "zaten `inert`" diye atlıyordu. İçteki kapanınca arka planın `inert`i
 * kalkıyor ve DIŞTAKİ katman hâlâ açıkken arka plan yeniden etkileşilebilir
 * oluyordu — kapatılan kusurun ta kendisi, bir katman derinden geri geliyordu.
 * Ölçüm (jsdom, iki katman tek çizimde): iç kapandıktan sonra `[inert]` sayısı
 * 3 → 0, dış katman hâlâ açıkken.
 *
 * Sayaç bunu kapatır: her katman kendi işaretini SAYAR, son çıkan ışığı söner.
 * Bizim olmayan (kalıcı) `inert` sayaca hiç girmez ve hiç sökülmez.
 */
const inertSayaci = new WeakMap<HTMLElement, number>();

/**
 * ARKA PLAN `inert` OLUR (QA turu 4 P3).
 *
 * ÖLÇÜLMÜŞ KUSUR: süzgeç çekmecesi açıkken katılımcı satırları `tabIndex: 0`,
 * `aria-hidden: null` ve arka plan `inert: false` kalıyordu. Fare yolu perde
 * sayesinde güvenliydi ama `aria-modal` desteği zayıf olan yardımcı teknoloji
 * eşleşmesinde ekran okuyucu satırı buluyor, etkinleştiriyor ve HİÇBİR geri
 * bildirim olmadan yalnız çekmece kapanıyordu (`?person=` hiç yazılmıyor).
 * `aria-modal` bir NİYET beyanıdır; `inert` davranışın kendisidir.
 *
 * Panelin ATA ZİNCİRİ boyunca kardeşler işaretlenir — gövdenin çocukları DEĞİL:
 * kit katmanı `createPortal` kullanmaz, panel çağıranın ağacında durur. Gövdeye
 * bakan naif bir uygulama panelin KENDİ atasını `inert` yapar ve katmanı da
 * söndürürdü.
 */
function inertBackground(panel: HTMLElement): () => void {
  const isaretlenen: HTMLElement[] = [];
  let node: HTMLElement | null = panel;
  while (node !== null && node !== document.body && node.parentElement !== null) {
    const parent: HTMLElement = node.parentElement;
    for (const kardes of Array.from(parent.children)) {
      if (kardes === node || !(kardes instanceof HTMLElement)) continue;
      const sayac = inertSayaci.get(kardes) ?? 0;
      /* Sayacı olmayan `inert` ağaç BİZİM DEĞİL: sökerken de dokunulmaz. */
      if (sayac === 0 && kardes.hasAttribute('inert')) continue;
      if (globalStatusOnly(kardes)) continue;
      inertSayaci.set(kardes, sayac + 1);
      kardes.setAttribute('inert', '');
      kardes.setAttribute(INERT_MARK, '');
      isaretlenen.push(kardes);
    }
    node = parent;
  }
  return () => {
    for (const element of isaretlenen) {
      const kalan = (inertSayaci.get(element) ?? 1) - 1;
      if (kalan > 0) {
        inertSayaci.set(element, kalan);
        continue;
      }
      inertSayaci.delete(element);
      element.removeAttribute('inert');
      element.removeAttribute(INERT_MARK);
    }
  };
}

/**
 * Global durum yüzeyi mi — kendisi ya da YALNIZ onu saran kap.
 *
 * Sarmalayıcı da atlanır (kabuk şeridi bir `div` içine alınırsa duyuru yine
 * kaybolurdu), ama yalnız içinde SEKME DURAĞI yoksa: durak taşıyan bir ağaç
 * arka plandır ve `inert` olmaktan muaf tutulamaz — muafiyet o zaman kapıyı
 * boşa düşürürdü.
 *
 * KOŞUL NİTELİĞİ TAŞIYANIN KENDİSİNE DE UYGULANIR (tech-lead ölçümü 17.09).
 * İlk hâlde `hasAttribute` KOŞULSUZ muaf yapıyordu: `data-ib-global-status`
 * yazan herhangi bir kap, içinde düğme olsa bile katmanın arkasında
 * ETKİLEŞİLEBİLİR kalıyordu (ölçüldü: düğme taşıyan sahte bir "global" öğe
 * `inert: false`). Öznitelik böylece serbest bir "beni inert yapma" kaçış
 * kapısıydı ve kapatılan P3'ü tek satırla geri açabiliyordu. Muafiyetin
 * gerekçesi DUYURUDUR (ekran okuyucu bağlantı kopuşunu duymalı), etkileşim
 * değil: durak taşıyan yüzey muafiyetini kaybeder. Taşıyıcılar ayrıca
 * `focus-surface.guard` içinde ENUMERE edilir.
 */
function globalStatusOnly(element: HTMLElement): boolean {
  const kendisiIsaretli = element.hasAttribute(GLOBAL_STATUS_ATTR);
  if (!kendisiIsaretli && element.querySelector(`[${GLOBAL_STATUS_ATTR}]`) === null) return false;
  const kendisiDurak = element.matches(FOCUSABLE_SELECTOR) && element.tabIndex >= 0;
  return !kendisiDurak && focusableWithin(element).length === 0;
}

/**
 * AÇIK KATMANLAR — Esc YALNIZ EN İÇTEKİ KATMANI KAPATIR (K1 tech-lead, 3b QA P3).
 *
 * Her katman Esc'i `window` üzerinde dinler. Katılımcı dizininde kişi katmanının
 * İÇİNDE görüşme diyaloğu açıkken tek Esc ikisini birden kapatıyor, URL'den
 * `person=` düşüyordu. Karar kaydolma SIRASINA bağlanmaz (React efektleri
 * çocuktan ebeveyne koşar, aynı çizimde açılan iki katmanda sıra tersine döner;
 * bkz. `inertSayaci`), DOM İÇERMESİNE bağlanır: kit katmanı `createPortal`
 * kullanmaz, iç katman dış katmanın panelinin içinde durur. Panelinin içinde
 * açık başka bir katman olan katman Esc'te kapanmaz; içteki kapanınca bir
 * sonraki Esc onu kapatır. Yan yana (iç içe olmayan) katmanların davranışı
 * değişmez.
 */
const acikKatmanlar = new Set<HTMLElement>();

function icindeAcikKatmanVar(panel: HTMLElement): boolean {
  return Array.from(acikKatmanlar).some((other) => other !== panel && panel.contains(other));
}

export interface DialogFocusOptions {
  readonly open: boolean;
  /** `role="dialog"` taşıyan kap; tuzak ve odak bu kapla sınırlıdır. */
  readonly panelRef: RefObject<HTMLElement | null>;
  /** Esc'in çağıracağı kapanış. Kimlik SABİT olmak zorunda değil (ref'te tutulur). */
  readonly onEscape: () => void;
  /** Modal katmanlarda arka plan kaydırması kilitlenir; açılır katmanlarda hayır. */
  readonly lockScroll?: boolean;
  /**
   * Odak taşınmadan önce beklenen koşul. Tarih seçicinin katmanı ölçülene kadar
   * `visibility: hidden` durur; görünmeyen öğe gerçek tarayıcıda ODAK ALMAZ.
   */
  readonly ready?: boolean;
  /** Açılışta odaklanacak öğe; verilmezse panelin kendisi (`tabIndex={-1}`). */
  readonly initialFocus?: (panel: HTMLElement) => HTMLElement | null;
  /**
   * Kapanışta odağın döneceği düğme — açan öğe okunamadığında kullanılır.
   * Fare tıklaması her tarayıcıda düğmeyi ODAKLAMAZ (Safari) ve testte hiç
   * odaklamaz; o durumda "açan öğe" `body` çıkar ve odak kaybolurdu.
   */
  readonly returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * Katman odak sözleşmesi: (1) açılışta odak panele girer, (2) Tab/Shift+Tab
 * panelin içinde sarılır, (3) kapanışta odak açan öğeye döner.
 *
 * Odak zaten panelin içindeyse (ör. `autoFocus` taşıyan güvenli eylem) odak
 * ÇALINMAZ — onay pencerelerinde varsayılan "Vazgeç"tir ve öyle kalır.
 */
export function useDialogFocus({
  open,
  panelRef,
  onEscape,
  lockScroll = false,
  ready = true,
  initialFocus,
  returnFocusTo,
}: DialogFocusOptions): void {
  const openerRef = useRef<Element | null>(null);
  const movedRef = useRef(false);
  const escapeRef = useRef(onEscape);
  const initialFocusRef = useRef(initialFocus);
  escapeRef.current = onEscape;
  initialFocusRef.current = initialFocus;

  /*
   * Açan öğe ÇİZİM sırasında yakalanır: panel DOM'a girerken `autoFocus` odağı
   * içeri alır, dolayısıyla efekt içinde bakmak geç kalır ve kapanışta odak
   * `body`ye düşerdi.
   */
  if (open && openerRef.current === null && typeof document !== 'undefined') {
    openerRef.current = document.activeElement;
  }
  /*
   * AÇAN ÖĞE YALNIZ GERÇEK KAPANIŞTA UNUTULUR (panel ölçümü 26.10). Önceden
   * efekt temizliği siliyordu; StrictMode `open` ile doğan katmanın efektini
   * söküp yeniden kurduğu için açan öğe daha ilk karede kayboluyor, gerçek
   * kapanışta odak `body`ye düşüyordu (panelin üç diyaloğu koşullu bağlanır).
   * Temizlik artık efektin KURULUŞTA yakaladığı değeri kullanır.
   */
  if (!open) openerRef.current = null;

  useEffect(() => {
    if (!open || !ready || movedRef.current) return;
    const panel = panelRef.current;
    if (panel === null) return;
    movedRef.current = true;
    if (panel.contains(document.activeElement)) return;
    (initialFocusRef.current?.(panel) ?? panel).focus();
  }, [open, ready, panelRef]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const panel = panelRef.current;
        if (panel !== null && icindeAcikKatmanVar(panel)) return;
        escapeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (panel === null) return;
      const stops = focusableWithin(panel);
      const active = document.activeElement;
      if (stops.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = stops[0] as HTMLElement;
      const last = stops[stops.length - 1] as HTMLElement;
      if (!(active instanceof Node) || !panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      /* Panelin kendisi odaktayken ileri Tab yerel davranışla ilk durağa gider;
         yalnız geri sarma ele alınır. */
      if (event.shiftKey && (active === panel || active === first)) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const { body } = document;
    /* Geri dönülecek düğme efekt BAŞINDA yakalanır: temizlikte `ref.current`
       okumak lint kuralının (ve gerçeğin) uyardığı bayat okuma olurdu. */
    const fallback = returnFocusTo?.current ?? null;
    const opener = openerRef.current;
    const previousOverflow = body.style.overflow;
    if (lockScroll) body.style.overflow = 'hidden';
    /*
     * `lockScroll` MODALLİK işaretidir: arka planı kilitleyen katman onu
     * `inert` de yapar. Tarih seçicinin katmanı (açılır, kilitlemez) hariçtir
     * ve olmalıdır — alanın KENDİSİ arka plandadır, `inert` yazmak kullanıcıyı
     * kendi girdisinden ederdi.
     */
    const panelForInert = lockScroll ? panelRef.current : null;
    const releaseInert = panelForInert === null ? null : inertBackground(panelForInert);
    const acikPanel = panelRef.current;
    if (acikPanel !== null) acikKatmanlar.add(acikPanel);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (acikPanel !== null) acikKatmanlar.delete(acikPanel);
      releaseInert?.();
      if (lockScroll) body.style.overflow = previousOverflow;
      movedRef.current = false;
      /*
       * Odak YALNIZCA kaybolduysa geri alınır. Kullanıcı katmanı kapatırken
       * başka bir alana tıkladıysa odağı ondan geri çalmak, "tuzak"ı katman
       * kapandıktan sonra da sürdürmek olurdu.
       */
      const active = document.activeElement;
      const lost = active === null || active === body || !active.isConnected;
      const opened =
        opener instanceof HTMLElement && opener !== body && opener.isConnected ? opener : null;
      const target = opened ?? fallback;
      if (lost && target !== null && target.isConnected) target.focus();
    };
  }, [open, panelRef, lockScroll, returnFocusTo]);
}

export interface MenuDismissOptions {
  readonly open: boolean;
  /** Tetikleyici + menüyü saran kap; dışarı tıklama buna göre ölçülür. */
  readonly rootRef: RefObject<HTMLElement | null>;
  /** Esc sonrası odağın döneceği düğme. */
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  /**
   * Esc ipucundan ÖNCE dinlenir (pencere, yakalama evresi). Varsayılan KAPALI:
   * kesik metin ipucusu açıkken ilk Esc yalnız ipucuyu kapatır, menü ikinci
   * basışta kapanır (`tooltip.tsx`, bildirim menüsü). Menü öğesinin KENDİSİ
   * kesik metin taşıyorsa (kurum değiştirici) ipucu her odakta açılır ve Esc
   * menüye hiç ulaşmaz; o yüzey bu seçenekle ilk basışta kapanır (QA K4 P2-3).
   */
  readonly escapeFirst?: boolean;
  /**
   * ROTA ANAHTARI (yeni görünüm 1c, 1a QA gözlemi ölçüldü): verilirse menü rota
   * değişince kapanır, AMA yalnız menü açıldıktan SONRA bir kullanıcı hareketi
   * (işaretçi, tuş, tarayıcı geri/ileri) olduysa. Programatik yönlendirme
   * (girişten sonra `/tr` → kurum ana sayfası) kullanıcının az önce açtığı
   * menüyü kapatmaz. Ölçüm: 375 px'te kurum hesabı girişten hemen sonra
   * "Menü"ye bastığında 6 denemenin 4'ünde menü açılıp yönlendirmeyle kapandı
   * (`aria-expanded=false`). Menü öğesine, sayfadaki bir bağlantıya ya da geri
   * düğmesine basınca rota değişimi menüyü yine kapatır.
   */
  readonly routeKey?: string;
}

/**
 * Menü (açılır liste) sözleşmesi: dışarı tıklama ve Esc kapatır, Esc'te odak
 * TETİKLEYİCİYE döner (APG "menu button": aksi hâlde odak `body`ye düşer ve
 * klavye kullanıcısı sayfanın başına savrulur).
 *
 * Menüde odak TUZAĞI YOKTUR ve olmamalıdır: menü tetikleyicinin hemen ardında
 * çizilir, Tab doğal olarak dışarı çıkar. Modal katman değildir; `aria-modal`
 * da taşımaz.
 *
 * TEK İSTİSNA (yeni görünüm 1c): dar ekranda ekranı kaplayan hesap menüsü
 * paneli (MobilWebKabuk kartı: "Odak menünün içinde tutulur, Esc kapatır").
 * Tuzak o yüzeyin kendisindedir ve yalnız 767 px altında kurulur; istisna
 * `focus-surface.guard.spec.tsx` envanterinde gerekçesiyle yazılıdır.
 */
export function useMenuDismiss({
  open,
  rootRef,
  triggerRef,
  onClose,
  escapeFirst = false,
  routeKey,
}: MenuDismissOptions): void {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const gestureSinceOpen = useRef(false);
  const lastRoute = useRef(routeKey);
  const tracksRoute = routeKey !== undefined;

  /* Menü açıkken kullanıcı hareketi kaydedilir; açan hareket SAYILMAZ (dinleyici açılıştan sonra kurulur). */
  useEffect(() => {
    gestureSinceOpen.current = false;
    if (!open || !tracksRoute || typeof window === 'undefined') return;
    const mark = () => {
      gestureSinceOpen.current = true;
    };
    document.addEventListener('pointerdown', mark, true);
    document.addEventListener('keydown', mark, true);
    window.addEventListener('popstate', mark);
    return () => {
      document.removeEventListener('pointerdown', mark, true);
      document.removeEventListener('keydown', mark, true);
      window.removeEventListener('popstate', mark);
    };
  }, [open, tracksRoute]);

  useEffect(() => {
    if (lastRoute.current === routeKey) return;
    lastRoute.current = routeKey;
    if (open && gestureSinceOpen.current) closeRef.current();
  }, [routeKey, open]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target)) return;
      closeRef.current();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      closeRef.current();
      triggerRef.current?.focus();
    };
    /* Menü açılınca kaydolur, yani sonradan açılan ipucunun dinleyicisinden ÖNCE koşar. */
    const keyTarget: Window | Document = escapeFirst ? window : document;
    document.addEventListener('mousedown', onPointerDown);
    keyTarget.addEventListener('keydown', onKeyDown as EventListener, escapeFirst);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      keyTarget.removeEventListener('keydown', onKeyDown as EventListener, escapeFirst);
    };
  }, [open, rootRef, triggerRef, escapeFirst]);
}
